import { motion, Target, TargetAndTransition, Transition } from 'framer-motion';
import moment from 'moment';
import { CSSProperties } from 'react';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { EditPencilIcon } from '~/images/icons';
import BulbIcon from '~/images/icons/BulbIcon';
import TrashIcon from '~/images/icons/Trash';
import { Colors } from '~/themes/colors';
import { isHolidayDate } from '~/utils/JobUtils';
import { Center } from '../Center';
import { Column } from '../Column';
import { Row } from '../Row';
import { Text } from '../Text';
import { BackTips } from './BackTips';
import { useTips } from './Hooks';
import { TipsButton } from './TipsButton';
import { IJobPreviewCardProps } from './Types';

const Animations = {
  CardContainer: {
    initial: { opacity: 0, y: -10 } as Target,
    animate: (isFlipped: boolean) =>
      ({ opacity: 1, y: 0, rotateY: isFlipped ? 180 : 0 }) as TargetAndTransition,
    exit: { opacity: 0 } as Target,
    transition: { duration: 0.6, exit: { duration: 0.2 }, layout: { duration: 0.3 } } as Transition,
  },
  BackView: {
    initial: { opacity: 0 } as Target,
    animate: { opacity: 1 } as TargetAndTransition,
    transition: {
      duration: 0.8,
    } as Transition,
  },
  FrontView: {
    initial: { opacity: 0 } as Target,
    animate: { opacity: 1 } as TargetAndTransition,
    transition: {
      duration: 0.8,
    } as Transition,
  },
};

const Styles = {
  CardContainer: (isFlipped: boolean, isForceHideTips: boolean, shouldShowTips: boolean) =>
    ({
      backgroundColor: Colors.white,
      width: '100%',
      maxHeight: 256,
      borderRadius: 8,
      border: `2px solid transparent`,
      ...(!isForceHideTips &&
        shouldShowTips && {
          border: `2px solid ${isFlipped ? Colors.secondary_25 : Colors.secondary_500}`,
        }),
      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 3px 10px 1px rgba(0, 0, 0, 0.15)',
    }) as CSSProperties,
  BackView: {
    transform: 'rotateY(180deg)',
    height: '100%',
  } as CSSProperties,
  FrontView: {
    position: 'relative',
    width: '100%',
    height: '100%',
  } as CSSProperties,
  CTAButton: {
    all: 'unset',
    cursor: 'pointer',
  } as CSSProperties,
};

export const JobPreviewCard = ({
  breakTime,
  date,
  endsAt,
  hourlyRate,
  isAssistedHygiene,
  isBreakTimePaid,
  isHideTips: isForceHideTips = false,
  profession,
  specialty,
  startsAt,
  subProfession,
  testName = 'job-preview',
  onDelete,
  onEdit,
}: IJobPreviewCardProps) => {
  const { isFlipped, shouldShowTips, toggle } = useTips({ date, hourlyRate, profession, startsAt });

  const isShowTips = !isForceHideTips && shouldShowTips;

  return (
    <motion.div
      animate={Animations.CardContainer.animate(isFlipped)}
      data-cy={`${testName}.container`}
      data-testid={`${testName}.container`}
      exit={Animations.CardContainer.exit}
      initial={Animations.CardContainer.initial}
      layout
      style={Styles.CardContainer(isFlipped, isForceHideTips, shouldShowTips)}
      transition={Animations.CardContainer.transition}
    >
      {!isFlipped ? (
        <motion.div
          animate={Animations.FrontView.animate}
          initial={Animations.FrontView.initial}
          style={Styles.FrontView}
          transition={Animations.FrontView.transition}
        >
          <Column width="100%">
            <Row
              alignItems="center"
              borderBottomColor={Colors.neutral_100}
              borderBottomStyle="solid"
              borderBottomWidth={1}
              justifyContent="space-between"
              marginBlock={18}
              marginInline={16}
              paddingBottom={8}
              marginBottom={10}
            >
              <Row alignItems="center" gap={12}>
                <Text
                  bold
                  data-cy={`${testName}.front-view.date`}
                  data-testid={`${testName}.front-view.date`}
                  fontSize={18}
                  marginLeft={10}
                  textTransform="capitalize"
                >
                  {moment.utc(date).format('ddd, MMM DD, YYYY')}
                </Text>
                {isHolidayDate(
                  moment.utc(date).set({ hour: 8, minute: 0, second: 0 }).toDate(),
                ) && (
                  <Text
                    backgroundColor={Colors.neutral_400}
                    bold
                    borderRadius={3}
                    color={Colors.white}
                    fontSize={12}
                    marginLeft={10}
                    paddingBlock={2}
                    paddingInline={7}
                    textTransform="uppercase"
                  >
                    holiday
                  </Text>
                )}
              </Row>

              <Row gap={16} marginRight={10}>
                <button
                  data-cy={`${testName}.front-view.cta-edit`}
                  data-testid={`${testName}.front-view.cta-edit`}
                  style={Styles.CTAButton}
                  type="button"
                  onClick={onEdit}
                >
                  <EditPencilIcon color={Colors.secondary_500} />
                </button>
                <button
                  data-cy={`${testName}.front-view.cta-delete`}
                  data-testid={`${testName}.front-view.cta-delete`}
                  style={Styles.CTAButton}
                  type="button"
                  onClick={onDelete}
                >
                  <TrashIcon color={Colors.secondary_500} />
                </button>
              </Row>
            </Row>

            <Column gap={4} marginBottom={26} marginInline={26} marginTop={0}>
              <Text
                data-cy={`${testName}.front-view.profession`}
                data-testid={`${testName}.front-view.profession`}
              >
                {UserSubProfession.getName(subProfession || '') ??
                  UserProfession.getName(profession)}
              </Text>
              <Text
                data-cy={`${testName}.front-view.specialty`}
                data-testid={`${testName}.front-view.specialty`}
              >
                {specialty?.split('_')?.[1] || 'General'}
              </Text>
              <Row alignItems="center" gap={4}>
                <Text
                  data-cy={`${testName}.front-view.hours`}
                  data-testid={`${testName}.front-view.hours`}
                >
                  {startsAt} - {endsAt}
                </Text>
                <Text>/</Text>
                <Text
                  data-cy={`${testName}.front-view.meal-break`}
                  data-testid={`${testName}.front-view.meal-break`}
                >
                  Meal Break: {breakTime} {isBreakTimePaid ? '(Paid)' : '(Unpaid)'}
                </Text>
              </Row>
              <Text
                data-cy={`${testName}.front-view.assisted-hygiene`}
                data-testid={`${testName}.front-view.assisted-hygiene`}
              >
                Assisted Hygiene Available: {isAssistedHygiene ? 'Yes' : 'No'}
              </Text>
              <Row alignItems="center" gap={2}>
                {isShowTips && <BulbIcon color={Colors.secondary_500} />}
                <Text
                  data-cy={`${testName}.front-view.hourly-rate`}
                  data-testid={`${testName}.front-view.hourly-rate`}
                >
                  ${hourlyRate}/hr
                </Text>
              </Row>
              {isShowTips && (
                <Center position="absolute" right={26} bottom={32}>
                  <TipsButton onClick={toggle} />
                </Center>
              )}
            </Column>
          </Column>
        </motion.div>
      ) : (
        <motion.div
          animate={Animations.BackView.animate}
          initial={Animations.BackView.initial}
          style={Styles.BackView}
          transition={Animations.BackView.transition}
        >
          <BackTips date={date} onReturnClick={toggle} />
        </motion.div>
      )}
    </motion.div>
  );
};
